import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Collapse,
  HStack,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import type { FlowStep, StepRun } from "../flow/types";
import { ACTOR_META, STATUS_META, copyText, formatClock } from "./statusMeta";
import { formatDuration } from "../flow/estimateTime";
import PollingIndicator from "./PollingIndicator";
import CodeBlock, { ApiCallRow } from "./ApiCallDetails";
import useNotification from "../../../utils/useNotification";

interface Props {
  step: FlowStep;
  run: StepRun;
  index: number;
  isTarget: boolean;
  isLast: boolean;
  isBranch?: boolean;
  onRetry?: () => void;
  onSkip?: () => void;
  onRestart?: () => void;
  onViewOrder?: () => void;
}

const MotionBox = motion(Box);

const useElapsed = (run: StepRun) => {
  const live = run.status === "running" || run.status === "waiting";
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!live) return;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [live]);
  if (!run.startedAt) return undefined;
  return (run.endedAt ?? (live ? now : run.startedAt)) - run.startedAt;
};

const StepCard: React.FC<Props> = ({ step, run, index, isTarget, isLast, isBranch, onRetry, onSkip, onRestart, onViewOrder }) => {
  const toast = useNotification();
  const meta = STATUS_META[run.status];
  const actor = ACTOR_META[step.actor];
  const elapsed = useElapsed(run);
  const isActive = run.status === "running" || run.status === "waiting";
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (run.status === "failed" || run.status === "waiting") setOpen(true);
  }, [run.status]);

  const failedCall = useMemo(
    () => (run.error?.apiCallId ? run.apiCalls.find((c) => c.id === run.error?.apiCallId) : undefined),
    [run.apiCalls, run.error]
  );
  const nonPollCalls = run.apiCalls.filter((c) => !c.isPoll);
  const pollCalls = run.apiCalls.filter((c) => c.isPoll);

  const errorText = useMemo(() => {
    if (!run.error) return "";
    const lines = [
      `Step: ${step.name} (${step.id})`,
      `Reason: ${run.error.reason}`,
      run.error.expected ? `Expected: ${run.error.expected}` : "",
      run.error.actual ? `Actual: ${run.error.actual}` : "",
      failedCall ? `API: ${failedCall.method} ${failedCall.url}` : "",
      failedCall ? `HTTP: ${failedCall.status ?? "ERR"}` : "",
      failedCall ? `Request: ${JSON.stringify(failedCall.requestBody ?? null)}` : "",
      failedCall ? `Response: ${JSON.stringify(failedCall.responseBody ?? failedCall.errorMessage ?? null)}` : "",
      failedCall ? `cURL: ${failedCall.curl}` : "",
    ];
    return lines.filter(Boolean).join("\n");
  }, [run.error, failedCall, step]);

  return (
    <HStack align="stretch" spacing={0}>
      <VStack spacing={0} w="32px" flexShrink={0} pt="14px">
        <Box position="relative" w="14px" h="14px">
          <Box
            w="14px"
            h="14px"
            borderRadius="full"
            bg={meta.dot}
            borderWidth="2px"
            borderColor="white"
            boxShadow={isActive ? `0 0 0 4px ${meta.ring}` : "0 0 0 1px var(--chakra-colors-gray-200)"}
            transition="all 0.3s"
          />
          {isActive && (
            <MotionBox
              position="absolute"
              inset={0}
              borderRadius="full"
              bg={meta.dot}
              animate={{ scale: [1, 2], opacity: [0.5, 0] }}
              transition={{ repeat: Infinity, duration: 1.4, ease: "easeOut" }}
            />
          )}
        </Box>
        {!isLast && <Box flex={1} w="2px" bg={run.status === "completed" ? "green.200" : "gray.200"} mt={1} transition="background 0.3s" />}
      </VStack>

      <MotionBox
        flex={1}
        mb={isLast ? 0 : 2}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: Math.min(index * 0.02, 0.3) }}
      >
        <Box
          borderWidth="1px"
          borderColor={isTarget && run.status === "pending" ? "blue.200" : meta.border}
          bg={meta.bg}
          borderRadius="lg"
          overflow="hidden"
          transition="border-color 0.3s, background 0.3s"
          boxShadow={isActive ? "sm" : undefined}
        >
          <HStack px={3} py={2.5} spacing={3} cursor="pointer" onClick={() => setOpen((o) => !o)} _hover={{ bg: "blackAlpha.50" }}>
            <Text fontSize="10px" color="gray.400" fontFamily="mono" w="24px">
              {isBranch ? "↳" : String(index + 1).padStart(2, "0")}
            </Text>
            <Box flex={1} minW={0}>
              <HStack spacing={2}>
                <Text fontSize="sm" fontWeight="600" color="gray.800" noOfLines={1}>
                  {step.name}
                </Text>
                <Badge colorScheme={actor.colorScheme} variant="subtle" fontSize="9px">
                  {actor.label}
                </Badge>
                {step.kind === "event" && (
                  <Badge colorScheme="orange" variant="outline" fontSize="9px">
                    event
                  </Badge>
                )}
                {step.optional && (
                  <Badge colorScheme="gray" variant="outline" fontSize="9px">
                    optional
                  </Badge>
                )}
                {isTarget && (
                  <Badge colorScheme="blue" fontSize="9px">
                    target
                  </Badge>
                )}
              </HStack>
              <Text fontSize="xs" color="gray.500" noOfLines={1}>
                {run.note || step.description}
              </Text>
            </Box>
            <HStack spacing={3} flexShrink={0}>
              {elapsed !== undefined && (
                <Text fontSize="10px" color="gray.500" fontFamily="mono">
                  {formatDuration(elapsed)}
                </Text>
              )}
              <HStack spacing={1.5} minW="120px" justify="flex-end">
                {isActive && <Spinner size="xs" color={meta.color} />}
                <Text fontSize="xs" fontWeight="700" color={meta.color}>
                  {meta.label}
                </Text>
              </HStack>
            </HStack>
          </HStack>

          <Collapse in={open} animateOpacity>
            <Box px={3} pb={3} borderTopWidth="1px" borderColor="blackAlpha.100" pt={3}>
              <HStack fontSize="10px" color="gray.500" spacing={4} mb={3} wrap="wrap">
                <Text>id {step.id}</Text>
                <Text>start {formatClock(run.startedAt)}</Text>
                <Text>end {formatClock(run.endedAt)}</Text>
                <Text>API calls {nonPollCalls.length}</Text>
                <Text>poll checks {pollCalls.length}</Text>
                <Text>events {run.eventsProcessed}</Text>
              </HStack>

              {run.poll && <Box mb={3}><PollingIndicator poll={run.poll} active={run.status === "waiting"} /></Box>}

              {run.error && (
                <Box mb={3} borderWidth="1px" borderColor="red.300" bg="white" borderRadius="md" p={3}>
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="xs" fontWeight="700" color="red.700">
                      Step failed
                    </Text>
                    <HStack>
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={async () => {
                          const ok = await copyText(errorText);
                          toast({ status: ok ? "success" : "error", description: ok ? "Error details copied" : "Copy failed" });
                        }}
                      >
                        Copy error
                      </Button>
                      {onViewOrder && (
                        <Button size="xs" variant="outline" onClick={onViewOrder}>
                          View order
                        </Button>
                      )}
                      {onRestart && (
                        <Button size="xs" variant="outline" onClick={onRestart}>
                          Restart flow
                        </Button>
                      )}
                      {onSkip && step.optional && (
                        <Button size="xs" variant="outline" colorScheme="gray" onClick={onSkip}>
                          Skip & continue
                        </Button>
                      )}
                      {onRetry && (
                        <Button size="xs" colorScheme="red" onClick={onRetry}>
                          Retry step
                        </Button>
                      )}
                    </HStack>
                  </HStack>
                  <VStack align="stretch" spacing={1} fontSize="xs">
                    <HStack align="baseline">
                      <Text w="80px" color="gray.500">
                        Reason
                      </Text>
                      <Text color="red.700" fontWeight="600">
                        {run.error.reason}
                      </Text>
                    </HStack>
                    {run.error.expected && (
                      <HStack align="baseline">
                        <Text w="80px" color="gray.500">
                          Expected
                        </Text>
                        <Text fontFamily="mono">{run.error.expected}</Text>
                      </HStack>
                    )}
                    {run.error.actual && (
                      <HStack align="baseline">
                        <Text w="80px" color="gray.500">
                          Actual
                        </Text>
                        <Text fontFamily="mono">{run.error.actual}</Text>
                      </HStack>
                    )}
                  </VStack>
                  {failedCall && (
                    <Box mt={3}>
                      <ApiCallRow call={failedCall} highlight defaultOpen />
                    </Box>
                  )}
                </Box>
              )}

              <Tabs size="sm" variant="soft-rounded" colorScheme="gray">
                <TabList>
                  <Tab fontSize="xs">API calls ({nonPollCalls.length})</Tab>
                  <Tab fontSize="xs">Polling ({pollCalls.length})</Tab>
                  <Tab fontSize="xs">Step info</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel px={0}>
                    <VStack align="stretch" spacing={2}>
                      {nonPollCalls.length ? (
                        nonPollCalls.map((c) => <ApiCallRow key={c.id} call={c} highlight={c.id === failedCall?.id} />)
                      ) : (
                        <Text fontSize="xs" color="gray.500">
                          No API calls recorded yet.
                        </Text>
                      )}
                    </VStack>
                  </TabPanel>
                  <TabPanel px={0}>
                    <VStack align="stretch" spacing={2}>
                      {pollCalls.length ? (
                        pollCalls.map((c) => <ApiCallRow key={c.id} call={c} />)
                      ) : (
                        <Text fontSize="xs" color="gray.500">
                          No state checks recorded for this step.
                        </Text>
                      )}
                    </VStack>
                  </TabPanel>
                  <TabPanel px={0}>
                    <VStack align="stretch" spacing={2}>
                      <CodeBlock title="Description" value={step.description} copyable={false} />
                      <CodeBlock
                        title="Definition"
                        value={{
                          id: step.id,
                          actor: step.actor,
                          kind: step.kind,
                          optional: Boolean(step.optional),
                          estimateMs: step.estimateMs,
                          hasVerify: Boolean(step.verify),
                        }}
                        copyable={false}
                      />
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
          </Collapse>
        </Box>
      </MotionBox>
    </HStack>
  );
};

export default StepCard;
